import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useWorkspaceTemplates } from 'Shared/utils/hooks/use-workspace-templates';
import { useAddMeetingItem } from 'Shared/utils/hooks/use-add-meeting-item';
import { useHandleFocusWorkspaceItem } from '../../../hooks/use-handle-focus-workspace-item';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';

// Types
import { Workspace } from 'Shared/types/Workspace.type';
import { AgendaBuilderSteps } from '../AgendaBuilder';
import { ItemType } from 'Shared/constants/types';

// Actions
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { removeTodo } from 'Shared/actions/todos';

// Components

import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import AgendaBuilderModalWrapper from '../AgendaBuilderModalWrapper';
import AgendaBuildGuide from './AgendaBuildGuide';
import DiscussionTopics from 'Dashboard/components/WorkspacePage/pages/WorkspaceHomeTab/WorkspaceContent/DiscussionTopics/DiscussionTopics';

// Styles
import './AgendaBuildScreen.scss';

type Props = {
  workspace: Workspace;
  handleSetState: Function;
};

const AgendaBuildScreen = ({ workspace, handleSetState }: Props) => {
  const dispatch = useDispatch();
  const { isApplyingTemplate, handleApplyTemplate } = useWorkspaceTemplates({ workspace });
  const { isMedium } = useResponsiveScreenWidth({ isAtLeast: true });

  const { handleAddMeetingItem } = useAddMeetingItem();
  const handleFocusWorkspaceItem = useHandleFocusWorkspaceItem({
    workspaceId: workspace.id,
  });

  const [guideStep, setGuideStep] = useState(0);

  const handleContinueClick = () => {
    handleSetState(AgendaBuilderSteps.apply);
  };

  const handleAddSectionClick = () => {
    handleAddMeetingItem({
      workspaceId: workspace.id,
      isSection: true,
      isAssignCurrentUser: false,
      itemType: ItemType.discussionTopic,
      position: workspace.discussion_topics.length + 1,
    }).then(response => {
      const uuid = response.data.data.uuid;
      handleFocusWorkspaceItem(uuid);
      dispatch(updateSelectedUuids(uuid, { isOneSelected: true, isShiftPressed: false }));
    });
  };

  useEffect(() => {
    if (workspace.discussion_topics.length === 0) {
      handleApplyTemplate({ template: 'weekly_one_on_one_meeting', sourcePage: 'template_wizard' });
    }
  }, []);

  const baseClassName = 'AgendaBuildScreen';
  const classes = classnames(
    baseClassName,
    !isMedium ? `${baseClassName}--full-width` : '',
    isMedium && guideStep !== -1 ? `${baseClassName}--guide-${guideStep}` : ''
  );

  const ActionsArea = ({ itemUuid }) => {
    const [isRemoving, toggleIsRemoving] = useToggle(false);
    const handleRemoveClick = () => {
      toggleIsRemoving();
      dispatch(removeTodo({ itemType: ItemType.discussionTopic, uuid: itemUuid }, { freshWorkspaceInRedux: true }));
    };

    return (
      <div className={`${baseClassName}__body__agenda-item__actions`}>
        <WpButtonGroup>
          <WpButton size="sm" variant="tertiary" rounded onClick={handleRemoveClick} loading={isRemoving}>
            <WpIcon name="trash" />
          </WpButton>
        </WpButtonGroup>
      </div>
    );
  };

  return (
    <AgendaBuilderModalWrapper
      workspace={workspace}
      handleSetState={handleSetState}
      handleContinueClick={handleContinueClick}
      blockScroll={isMedium && guideStep !== -1}
    >
      <div className={classes}>
        <h5 className={`${baseClassName}__title`}>
          Use this template to structure your 1:1s with sections and discussion topics.
        </h5>
        <div className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__body__agenda`}>
            {isApplyingTemplate ? (
              <>loading...</>
            ) : (
              <div className={`${baseClassName}__body__agenda__topics`}>
                <DiscussionTopics
                  discussionTopicsTodosContentRef={null}
                  onFocusedTodoUpdate={null}
                  showToolbar={false}
                  customActionsArea={ActionsArea}
                  isCompleteToggleDisabled={true}
                />
                <div className={`${baseClassName}__body__agenda__topics__add-btn`}>
                  <WpButton variant="secondary" onClick={handleAddSectionClick}>
                    <WpIcon name="plus-circle" />
                    Add Section
                  </WpButton>
                </div>
              </div>
            )}
          </div>
          {isMedium && (
            <div className={`${baseClassName}__body__guide`}>
              <AgendaBuildGuide guideStep={guideStep} setGuideStep={setGuideStep} />
            </div>
          )}
        </div>
      </div>
    </AgendaBuilderModalWrapper>
  );
};

export default AgendaBuildScreen;
